/* eslint-disable react-hooks/immutability */
import type {
  CellData,
  Column,
  RowData,
  TableFeature,
  TableOptions,
} from '@tanstack/react-table';
import { isEmpty, isNumber } from 'lodash-es';
import { useEffect, useMemo, useRef, type JSX } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import type { MeterBarChartColumnTypeFeatureConstructors } from './meterbar-chart-types.js';
import { MultiMeterBarChart } from '../../../../charts/meter-bar/multi-meter-bar/MultiMeterBarChart.js';
import { METER_BAR_SIZE } from '../../../../charts/meter-bar/shared/constants.js';
import type { MeterBarChartElementRef } from '../../../../charts/meter-bar/shared/types/meter-bars.js';
import { MeterBarChart } from '../../../../charts/meter-bar/single-meter-bar/MeterBarChart.js';
import { devLog } from '../../../../core/utils/logging.js';
import { isDisplayColumnDefConfig } from '../../hooks/useTable/datatable-typeguards.js';
import type {
  DataTableColumnDef,
  DataTableDisplayColumnDef,
  DataTableRowData,
} from '../../public.api.js';
import type { DataTableMeterbarColumnConfig } from '../ColumnTypes/column-types-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/** Default max value for meterbar column. */
const DEFAULT_MAX_VALUE = 100;

/**
 * Type guard function ensures that the defined config in the column definition contains certain properties.
 * @internal
 */
function isDataTableMeterBarColumnConfig(
  config: unknown,
): config is DataTableMeterbarColumnConfig {
  return (
    config !== null &&
    config !== undefined &&
    typeof config === 'object' &&
    ('max' in config ||
      'color' in config ||
      'threshold' in config ||
      'showTooltip' in config ||
      'max' in config)
  );
}

/**
 * Type guard function ensures that the defined column contains the 'accessor' property.
 * @internal
 */
function isColumnDefWithAccessor<TData extends DataTableRowData, TValue>(
  column: unknown,
): column is DataTableDisplayColumnDef<TData, TValue> {
  return (
    column !== null &&
    column !== undefined &&
    typeof column === 'object' &&
    'accessor' in column
  );
}

type MeterBarChartRendererType<
  TData extends RowData,
  TValue extends CellData = CellData,
> = {
  value: number | { name: string; value: number; color?: string }[];
  column: Column<TDataTableFeatures, TData, TValue>;
};

/**
 * Type guard to check value against MultiMeterBar values.
 * @internal
 */
export function isMultiMeterBarData(
  value: unknown,
): value is Array<{ name: string; value: number; color?: string }> {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        typeof item.name === 'string' &&
        typeof item.value === 'number' &&
        (item.color === undefined || typeof item.color === 'string'),
    )
  );
}

/**
 * Intended for configuring and rendering the meterbar chart component.
 */
export function MeterBarChartRenderer<
  TData extends RowData,
  TValue extends CellData = CellData,
>({
  value,
  column,
}: Readonly<MeterBarChartRendererType<TData, TValue>>): JSX.Element {
  const chartRef = useRef<MeterBarChartElementRef>(null);

  let config: DataTableMeterbarColumnConfig = {};
  if (
    column?.columnDef.meta &&
    isDataTableMeterBarColumnConfig(column.columnDef.meta.config)
  ) {
    config = column.columnDef.meta.config;
  }

  const columnId = column.columnDef.id ?? '';

  const maxValuesFromState = column.table.atoms.meterbarChartMaxValues?.get();

  const maxValue = !isEmpty(maxValuesFromState)
    ? maxValuesFromState[columnId]
    : DEFAULT_MAX_VALUE;

  const listenerRef = useRef<((e?: PointerEvent) => void) | null>(null);

  useEffect(() => {
    // clean possible leftover event on unmount
    return () => {
      listenerRef.current?.();
    };
  }, []);

  const registerUnpinOnPointerUp = config?.showTooltip
    ? () => {
        if (listenerRef.current) {
          return;
        }
        const handler = (event: PointerEvent) => {
          if (
            (event.target as HTMLElement).closest('[role="tooltip"]') !== null
          ) {
            return;
          }

          chartRef.current?.unpinTooltip();
          listenerRef.current?.();
          listenerRef.current = null;
        };
        listenerRef.current = () =>
          document.removeEventListener('pointerup', handler);
        document.addEventListener('pointerup', handler);
      }
    : undefined;

  // simple MeterBar
  if (typeof value === 'number') {
    return (
      <span
        style={{ display: 'contents' }}
        onClick={registerUnpinOnPointerUp}
        role="none"
      >
        <MeterBarChart
          value={value}
          min={config?.min}
          max={maxValue}
          color={config?.color as string}
          size={METER_BAR_SIZE.Size8}
          width="100%"
          height="auto"
          style={{
            pointerEvents: 'auto',
            padding: `${Spacings.Size6} 0`,
          }}
          formatter={config?.formatter}
          ref={chartRef}
        >
          <MeterBarChart.Tooltip hidden={!config?.showTooltip} />
          {config?.thresholds?.map((threshold) => (
            <MeterBarChart.ThresholdIndicator
              key={`threshold-${threshold.color}-${threshold.value}`}
              // DEPRECATION: [APPDEV-15843] BREAKING-CHANGE: Remove `name` from `thresholds`
              // eslint-disable-next-line @typescript-eslint/no-deprecated
              name={threshold.name}
              color={threshold.color}
              value={threshold.value}
              showIndicator={threshold.showIndicator}
            />
          ))}
        </MeterBarChart>
      </span>
    );
  }

  const isMultiMeterData = isMultiMeterBarData(value);
  if (!isMultiMeterData) {
    devLog('Invalid meter chart data.');
  }

  return (
    <span
      style={{ display: 'contents' }}
      onClick={registerUnpinOnPointerUp}
      role="none"
    >
      <MultiMeterBarChart
        size="size8"
        min={config?.min}
        max={maxValue}
        colorPalette={config?.colorPalette || 'categorical'}
        width="100%"
        height="auto"
        style={{
          pointerEvents: 'auto',
          padding: `${Spacings.Size6} 0`,
        }}
        formatter={config?.formatter}
        ref={chartRef}
      >
        {
          // only if this is multi meter data render segments
          isMultiMeterData &&
            value.map((data) => (
              <MultiMeterBarChart.Segment
                key={`${data.name}-${data.value}`}
                name={data.name}
                value={data.value}
                color={data?.color}
              />
            ))
        }
        <MultiMeterBarChart.Tooltip
          variant="shared"
          hidden={!config?.showTooltip}
        />
      </MultiMeterBarChart>
    </span>
  );
}

/**
 * Gets the highest total max values in a meterbar column.
 *
 * @param data - table data
 * @param columns - table column definition
 * @returns record of highest total max value per column
 * @internal
 */
export function getTotalMaxFromMeterbarChartData<
  TData extends DataTableRowData,
>(data: TData[], columns: DataTableColumnDef<TData>[]): Record<string, number> {
  const meterbarColumns = columns.filter(
    (columnDef) =>
      isDisplayColumnDefConfig<TData>(columnDef) &&
      columnDef.columnType === 'meterbar',
  );

  const maxValuesStorage: Record<string, number> = {};

  for (const row of data) {
    for (const columnDef of meterbarColumns) {
      if (isColumnDefWithAccessor(columnDef)) {
        const { accessor, config, id: columnId } = columnDef;

        const configuredMax: number | 'data-max' | undefined =
          isDataTableMeterBarColumnConfig(config) ? config.max : undefined;

        const meterbarValue = row as Record<string, unknown>;

        const meterbarDataEntry =
          typeof accessor === 'string'
            ? meterbarValue[accessor]
            : accessor(row);

        let totalValue = DEFAULT_MAX_VALUE;

        if (isNumber(configuredMax)) {
          maxValuesStorage[columnId] = configuredMax;
          continue;
        }

        if (meterbarDataEntry) {
          if (Array.isArray(meterbarDataEntry)) {
            totalValue = meterbarDataEntry.reduce(
              (acc, current) => acc + current.value,
              0,
            );
          } else {
            const entry = isNumber(meterbarDataEntry)
              ? meterbarDataEntry
              : DEFAULT_MAX_VALUE;
            totalValue =
              configuredMax === 'data-max'
                ? entry
                : Math.max(entry, DEFAULT_MAX_VALUE);
          }
        }

        maxValuesStorage[columnId] = Math.max(
          maxValuesStorage[columnId] ?? totalValue,
          totalValue,
        );
      }
    }
  }

  return maxValuesStorage;
}

/**
 * Configuration hook for the DataTable Meterbar Chart feature.
 * @internal
 */
export function useMeterBarChartMaxValuesCalc<TData extends DataTableRowData>(
  props: {
    data: TData[];
    columns: DataTableColumnDef<TData>[];
  },
  options: TableOptions<TDataTableFeatures, Exclude<TData, unknown>>,
): void {
  const meterbarChartMaxValues = useMemo(
    () => getTotalMaxFromMeterbarChartData<TData>(props.data, props.columns),
    [props.columns, props.data],
  );

  options.state = {
    ...options.state,
    meterbarChartMaxValues,
  };
}

export function constructMeterBarChartColumnTypeFeature(): TableFeature<MeterBarChartColumnTypeFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        meterbarChartMaxValues: null,
        ...initialState,
      };
    },
  };
}

/**
 * DataTableMeterBarChartColumnTypeFeature is the plugin that provides the loading state for the DataTable,
 * which can be used to display a loading indicator while the table's columns and/or data are being loaded.
 */
export const DataTableMeterBarChartColumnTypeFeature =
  constructMeterBarChartColumnTypeFeature();
